import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import styles from './EventCheckinPage.module.css';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '../utils/apiError';

const EventCheckinPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation('checkin');
  const { t: tApi } = useTranslation('apiErrors');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventInfo, setEventInfo] = useState(null);
  const canceledStatuses = new Set([
    'cancellation_pending_payment',
    'canceled',
    'refunding',
    'cancellation_completed',
    'cancellation_failed',
  ]);

  useEffect(() => {
    const fetchTickets = async () => {
      setLoading(true);
      setError('');
      try {
        let currentEvent = null;

        try {
          const evRes = await axios.get(`${process.env.REACT_APP_API_URL}/events/${id}`);
          const e = evRes.data?.event || null;
          if (e) {
            const status = String(e.cancellation_status || 'none');
            if (canceledStatuses.has(status)) {
              const eventPathId = e.slug || id;
              navigate(`/events/${eventPathId}/cancelled`, { replace: true, state: { eventName: e.name || '' } });
              return;
            }
            currentEvent = e;
            setEventInfo(e);
            setEventName(e.name || '');
          }
        } catch (_) {
          // ignore; tickets fallback below
        }

        const res = await axios.get(`${process.env.REACT_APP_API_URL}/my-tickets`);
        const data = res.data;
        const targetEventPublicId = currentEvent?.id ?? null;  // evt_xxx
        const eventTickets = (data.tickets || []).filter((t) => {
          // event.id は public_id (evt_xxx)
          const ticketEventPublicId = t?.event?.id;
          if (targetEventPublicId != null && ticketEventPublicId != null) {
            return ticketEventPublicId === targetEventPublicId;
          }
          // フォールバック: slug で比較
          const ticketEventSlug = t?.event?.slug;
          return String(ticketEventSlug || '') === String(id);
        });
        setTickets(eventTickets);
        const evObj = eventTickets[0]?.event || null;
        const nameFromTickets = evObj?.name || eventTickets[0]?.event_name || '';
        if (nameFromTickets && !currentEvent) setEventName(nameFromTickets);
        if (evObj && !currentEvent) {
          setEventInfo(evObj);
        }
      } catch (e) {
        setError(e.message || t('loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    fetchTickets();
  }, [id, navigate]);

  const [confirmTicketId, setConfirmTicketId] = useState(null);

  const performCheckin = async (ticketId) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/checkin`, {
        public_ticket_id: ticketId,
      });
      // refresh
      setTickets(prev => prev.map(t => t.public_ticket_id === ticketId ? { ...t, status: 'checked_in', checked_in_at: new Date().toISOString() } : t));
    } catch (e) {
      const msg = getApiErrorMessage(e, tApi);
      alert(msg);
    } finally {
      setConfirmTicketId(null);
    }
  };

  if (!user) return <div>{t('loginRequired')}</div>;
  if (loading) return <div>{t('loading')}</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>{t('heading')}</h2>

      <div className={styles.summary}>
        <span className={styles.summaryLabel}>
          {t('ticketsLabel')}{eventName ? `  ${eventName}` : ''}
        </span>
        <span className={styles.summaryCount}>{t('ticketsCount', { count: tickets.length })}</span>
      </div>

      {tickets.length === 0 ? (
        <div className={styles.emptyText}>{t('noTickets')}</div>
      ) : (
        <div className={styles.ticketList}>
          {tickets.map(ticket => {
            const ev = ticket.event || eventInfo || {};
            const prices = Array.isArray(ev.prices) ? ev.prices : (
              Array.isArray(ev.price_groups)
                ? ev.price_groups.flatMap(g => (Array.isArray(g.prices) ? g.prices.map(p => ({ ...p, group_id: g.id })) : []))
                : []
            );
            const priceRow = prices.find(p => String(p.id) === String(ticket.event_price_id)) || null;
            const priceType = priceRow ? (priceRow.price_type || priceRow.type2 || '') : '';
            let groupType = '';
            if (priceRow) {
              const groups = Array.isArray(ev.price_groups) ? ev.price_groups : [];
              const grp = groups.find(g => String(g.id) === String(priceRow.group_id));
              groupType = grp && grp.type ? grp.type : '';
            }

            const rawDate = eventInfo?.date || ev?.date || null;
            const dateStr = rawDate ? String(rawDate).slice(0, 10).replace(/-/g, '.') : '';
            const venueName = eventInfo?.venue || ev?.venue || '';
            const venueAddr = eventInfo?.address || ev?.address || '';
            const venueStr = venueName
              ? `${venueName}${venueAddr ? ` (${venueAddr})` : ''}`
              : venueAddr;

            const isCheckedIn = ticket.status === 'checked_in';
            const checkedAt = ticket.checked_in_at
              ? (() => {
                  const d = new Date(ticket.checked_in_at);
                  const yyyy = d.getFullYear();
                  const mm = String(d.getMonth() + 1).padStart(2, '0');
                  const dd = String(d.getDate()).padStart(2, '0');
                  const hh = String(d.getHours()).padStart(2, '0');
                  const min = String(d.getMinutes()).padStart(2, '0');
                  return `${yyyy}/${mm}/${dd} ${hh}:${min}`;
                })()
              : null;

            return (
              <div key={ticket.id} className={styles.ticketCard}>
                <div className={styles.ticketHeader}>
                  <span className={styles.ticketIcon}>🎫</span>
                  <span className={styles.ticketCode}>{ticket.public_ticket_id || t('noId')}</span>
                </div>

                {(dateStr || venueStr) && (
                  <div className={styles.ticketMeta}>
                    {dateStr && (
                      <div className={styles.metaRow}>
                        <span className={styles.metaIcon}>📅</span>
                        <span>{dateStr}</span>
                      </div>
                    )}
                    {venueStr && (
                      <div className={styles.metaRow}>
                        <span className={styles.metaIcon}>📍</span>
                        <span>{venueStr}</span>
                      </div>
                    )}
                  </div>
                )}

                {(groupType || priceType) && (
                  <div className={styles.tags}>
                    {groupType && <span className={styles.tag}>{groupType}</span>}
                    {priceType && <span className={styles.tag}>{priceType}</span>}
                  </div>
                )}

                <div className={styles.ticketFooter}>
                  <div className={styles.statusBlock}>
                    <div className={styles.statusRow}>
                      <span className={`${styles.statusDot} ${isCheckedIn ? styles.dotGray : styles.dotGreen}`} />
                      <span className={isCheckedIn ? styles.statusChecked : styles.statusPending}>
                        {isCheckedIn ? t('checkedIn') : t('notCheckedIn')}
                      </span>
                    </div>
                    {checkedAt && <div className={styles.checkedTime}>({checkedAt})</div>}
                  </div>
                  {!isCheckedIn && (
                    <button
                      onClick={() => setConfirmTicketId(ticket.public_ticket_id)}
                      className={styles.checkinBtn}
                      disabled={!ticket.public_ticket_id}
                    >
                      {t('checkinBtn')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirmTicketId && (
        <div className={styles.modalOverlay} role="dialog" aria-modal="true">
          <div className={styles.modal}>
            <h3>{t('modal.title')}</h3>
            <p>{t('modal.body')}</p>
            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={() => setConfirmTicketId(null)}>{t('modal.cancel')}</button>
              <button className={styles.confirmBtn} onClick={() => performCheckin(confirmTicketId)}>{t('modal.confirm')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventCheckinPage;